import {
  HStack, Box, Text, Checkbox, VStack, Portal,
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  Menu, MenuButton, MenuList, MenuItem, IconButton, Flex,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { FiChevronDown, FiMoreHorizontal } from 'react-icons/fi';

const FILTER_META = {
  project_id:    { label: 'Projects',   placeholder: 'Select Projects' },
  issue_type_id: { label: 'Issue Type', placeholder: 'Select Task Type' },
  status:        { label: 'Status',     placeholder: 'Status' },
};

const STATIC_OPTIONS = {
  issue_type_id: [
    { label: 'Task',  value: '1' },
    { label: 'Story', value: '2' },
    { label: 'Bug',   value: '3' },
  ],
  status: [
    { label: 'To Do',       value: 'TO DO' },
    { label: 'In Progress', value: 'IN PROGRESS' },
    { label: 'Review',      value: 'REVIEW' },
    { label: 'Done',        value: 'DONE' },
  ],
};

// Single inline dropdown filter
const FilterDropdown = ({ filterKey, meta, options, selected, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedLabels = options
    .filter(o => selected.includes(String(o.value)))
    .map(o => o.label);

  const displayLabel = selectedLabels.length
    ? selectedLabels.join(', ')
    : meta.placeholder;

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      strategy="fixed"
      data-tour="dashboard-filters"
    >
      <PopoverTrigger>
        <Flex
          align="center"
          justify="space-between"
          px={3}
          py={2}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          cursor="pointer"
          minW="160px"
          maxW="220px"
          bg="white"
          gap={2}
          onClick={() => setIsOpen(prev => !prev)}
          _hover={{ borderColor: 'gray.400' }}
        >
          <Text
            fontSize="sm"
            color={selectedLabels.length ? 'gray.800' : 'gray.500'}
            isTruncated
            mb={0}
          >
            {displayLabel}
          </Text>
          <Box as={FiChevronDown} color="gray.400" flexShrink={0} />
        </Flex>
      </PopoverTrigger>

      <Portal>
        <PopoverContent width="220px" zIndex={1500} boxShadow="lg">
          <PopoverBody p={3}>
            <Text fontSize="sm" fontWeight="semibold" mb={3}>
              {meta.label}
            </Text>
            <VStack align="start" spacing={2} maxH="220px" overflowY="auto">
              {options.map(opt => (
                <Checkbox
                  key={opt.value}
                  isChecked={selected.includes(String(opt.value))}
                  onChange={() => onToggle(filterKey, opt.value)}
                  size="sm"
                >
                  {opt.label}
                </Checkbox>
              ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const FilterChips = ({ filters, onChange, projectList = [] }) => {
  const safeProjects = Array.isArray(projectList) ? projectList : [];

  const getOptions = (key) => {
    if (key === 'project_id') {
      return safeProjects.map(p => ({
        label: p.project_name,
        value: p.project_id,
      }));
    }
    return STATIC_OPTIONS[key] ?? [];
  };

  const handleToggleOption = (key, value) => {
    const current = filters[key] ?? [];
    const asString = current.map(String);

    const updated = asString.includes(String(value))
      ? asString.filter(v => v !== String(value))
      : [...asString, String(value)];

    onChange({ ...filters, [key]: updated });
  };

  const handleClearAll = () => {
    const cleared = Object.keys(filters).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    onChange(cleared);
  };

  const activeFilters = Object.entries(filters).filter(
    ([key]) => key in FILTER_META
  );

  if (activeFilters.length === 0) return null;

  return (
    <HStack mt={3} spacing={2} flexWrap="wrap" align="center">
      {activeFilters.map(([key, value]) => {
        const meta = FILTER_META[key];
        const options = getOptions(key);
        const selected = Array.isArray(value) ? value.map(String) : [];

        return (
          <FilterDropdown
            key={key}
            filterKey={key}
            meta={meta}
            options={options}
            selected={selected}
            onToggle={handleToggleOption}
            data-tour="dashboard-filters"
          />
        );
      })}

      {/* Three dots menu — Clear All */}
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<FiMoreHorizontal />}
          variant="outline"
          size="sm"
          borderRadius="md"
          aria-label="More options"
          borderColor="gray.200"
          color="gray.500"
          _hover={{ borderColor: 'gray.400', bg: 'gray.50' }}
        />
        <MenuList zIndex={1500} minW="140px">
          <MenuItem
            fontSize="sm"
            color="red.500"
            onClick={handleClearAll}
            _hover={{ bg: 'red.100' }}
          >
            Clear All Filters
          </MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  );
};

export default FilterChips;